import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useDebouncedCallback } from "use-debounce";

import { Icon, icons } from "../../../components/Icon";
import { createNotification } from "../../../modules/notificationManager";
import { pushAdTrackingEvent } from "../../../utils/adTracking";
import { ButtonSection, PrimaryButton } from "../../AuthNew/shared/authStyles";
import { motion } from "../../AuthNew/shared/motionUtils";
import {
  ModalSubtitle,
  ModalTitle,
  slideInRight,
} from "../shared/onboardingStyles";
import { organizationSchema } from "../shared/validationSchemas";
import OrganizationInput from "./components/OrganizationInput";

const genericDomains = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "protonmail.com",
  "icloud.com",
  "mail.com",
  "zoho.com",
  "gmx.com",
  "yandex.com",
  "inbox.com",
  "fastmail.com",
  "yahoo.co.uk",
  "yahoo.co.in",
  "hotmail.co.uk",
  "hotmail.fr",
  "live.com",
  "live.co.uk",
  "outlook.co.uk",
  "outlook.in",
  "protonmail.ch",
  "rediffmail.com",
  "tutanota.com",
  "hushmail.com",
  "runbox.com",
  "posteo.de",
  "startmail.com",
];

// Progress Bar (Rectangle 5)
const ProgressBarContainer = styled.div`
  width: 100%;
  height: 10px;
  background: #eef1f7;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0;
  position: relative;
`;

const ProgressBarFill = styled(motion.div)`
  height: 100%;
  background: #aa8ef6;
  border-radius: 4px;
  transition: width 0.5s ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
`;

// Invitation List Components
const InvitationListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-top: 32px;
`;

const InvitationListTitle = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: #000e2a;
  margin: 0 0 12px 0;
`;

const InvitationListItem = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 4px 12px;
  border: 1px solid #cccfd4;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    border-color: #999faa;
    background: #f9fafb;
  }
`;

const InvitationIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: ${({ color, theme }) => color || theme.colors.main.authBlueLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.main.authBlue};
  flex-shrink: 0;
`;

const InvitationName = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: #000e2a;
  margin: 0;
  flex: 1;
`;

// Main Content Container
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  max-width: 400px;
`;

const Step1Organization = ({
  data,
  onChange,
  onNext,
  userInfo,
  onBack,
  fromInvitation = false,
  selectedOrganization,
  pendingInvitation,
  progress = 16.67, // Default progress for step 1 of 6
}) => {
  const location = useLocation();
  const history = useHistory();
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitted },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(organizationSchema),
    mode: "onSubmit",
    defaultValues: {
      organizationName: data.organizationName || "",
    },
  });

  const currentValue = watch("organizationName");

  // Generate smart suggestions based on user's name and email
  const generateSmartSuggestions = useCallback(() => {
    const suggestions = [];

    // Use first_name and last_name from userInfo
    if (userInfo?.first_name) {
      const firstName = String(userInfo.first_name).trim();
      suggestions.push(`${firstName}'s Agency`);
      suggestions.push(`${firstName} Company`);
    }

    if (userInfo?.last_name && userInfo.last_name !== userInfo.first_name) {
      const lastName = String(userInfo.last_name).trim();
      suggestions.push(`${lastName} & Associates`);
    }

    // Extract company name from email domain
    if (userInfo?.email) {
      const email = userInfo.email;
      const domain = email.split("@")[1];

      if (domain && !genericDomains.includes(domain)) {
        const companyName = domain.split(".")[0];
        if (companyName && companyName.length > 2) {
          // Capitalize first letter and make it more readable
          const formattedName =
            companyName.charAt(0).toUpperCase() +
            companyName.slice(1).replace(/[-_]/g, " ");
          suggestions.push(formattedName);
        }
      }
    }

    // Add some generic suggestions as fallback
    suggestions.push("My Agency", "My Company", "My Team");

    // Remove duplicates and limit to 3 suggestions
    return [...new Set(suggestions)].slice(0, 3);
  }, [userInfo]);

  const organizationSuggestions = generateSmartSuggestions();

  // Titles and subtitles for organization creation
  const modalTitle = "Let's create your workspace";
  const modalSubtitle = "Your personal hub for time tracking and productivity";

  const onSubmit = useCallback(
    formData => {
      onChange({ organizationName: formData.organizationName });
      onNext();
    },
    [onChange, onNext],
  );

  // Handle Enter key press
  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    },
    [handleSubmit, onSubmit],
  );

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // Track organization creation step when component is displayed
  useEffect(() => {
    pushAdTrackingEvent({
      event: "signup_verification_completed",
      location,
      selectedOrganization,
      full_name:
        userInfo?.first_name && userInfo?.last_name
          ? `${userInfo.first_name} ${userInfo.last_name}`
          : userInfo?.first_name || null,
      email: userInfo?.email || null,
    });
  }, []);

  // Update form value when data changes
  useEffect(() => {
    setValue("organizationName", data.organizationName || "");
  }, [data.organizationName, setValue]);

  // Handle invitation acceptance
  const handleInvitationAccept = useDebouncedCallback(
    invitation => {
      if (!invitation?.key) {
        createNotification(
          "error",
          "The invitation link is broken. Please contact your organization administrator for a new invitation link.",
        );
        return;
      }

      // Navigate to accept invitation page
      history.push({
        pathname: "/accept-invitation",
        search: new URLSearchParams({
          key: invitation.key,
        }).toString(),
      });
    },
    3000,
    { leading: true, trailing: false },
  );

  // Normalize invitations to array format
  const invitations = pendingInvitation
    ? Array.isArray(pendingInvitation)
      ? pendingInvitation
      : [pendingInvitation]
    : [];

  return (
    <motion.div
      variants={slideInRight}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Progress Bar (Rectangle 5) */}
      <ProgressBarContainer>
        <ProgressBarFill
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        />
      </ProgressBarContainer>

      {/* Title and Subtitle */}
      <div style={{ marginTop: "32px" }}>
        <ModalTitle>{modalTitle}</ModalTitle>
        <ModalSubtitle>{modalSubtitle}</ModalSubtitle>
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <ContentWrapper style={{ marginTop: "32px" }}>
          {/* Organization Name Input */}
          <OrganizationInput
            control={control}
            errors={errors}
            isSubmitted={isSubmitted}
            organizationSuggestions={organizationSuggestions}
            currentValue={currentValue}
            onSuggestionClick={suggestion => {
              setValue("organizationName", suggestion);
              onChange({ organizationName: suggestion });
            }}
          />

          {/* Pending Invitations */}
          {invitations.length > 0 && (
            <InvitationListContainer>
              <InvitationListTitle>
                Teams Invited you to join
              </InvitationListTitle>
              {invitations.map((invitation, index) => {
                const orgName = invitation?.organization_name || "Organization";
                const firstLetter = orgName.charAt(0).toUpperCase();
                return (
                  <InvitationListItem
                    key={invitation?.key || index}
                    type="button"
                    onClick={() => handleInvitationAccept(invitation)}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <InvitationIcon color={invitation?.color}>
                      {firstLetter}
                    </InvitationIcon>
                    <InvitationName>{orgName}</InvitationName>
                  </InvitationListItem>
                );
              })}
            </InvitationListContainer>
          )}
        </ContentWrapper>

        {/* Next Button */}
        <ButtonSection marginTop="32px">
          <PrimaryButton
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Next
          </PrimaryButton>
        </ButtonSection>
      </form>
    </motion.div>
  );
};

export default Step1Organization;
